import { useState, useEffect } from "react";
import PublicLayout from "../components/PublicLayout";
import { Server, Terminal, Laptop, Globe, ArrowRight, Shield, Zap, ChevronDown, ChevronUp, HelpCircle, Network, Code, Settings } from "lucide-react";

const FRAMEWORKS = {
    nextjs: {
        name: "Next.js",
        port: "3000",
        cmd: "synapselink 3000 --subdomain my-next-app",
        code: `// Start your local Next.js dev server
npm run dev

// In a new terminal, launch the tunnel
slink 3000 --subdomain my-next-app

// Output:
// ➔ Tunnel established: https://my-next-app.synapselink.cloud
// ➔ Routing traffic directly to http://localhost:3000`
    },
    vite: {
        name: "Vite / React",
        port: "5173",
        cmd: "synapselink 5173 --subdomain react-preview",
        code: `// Start your local Vite dev server
npm run dev

// In a new terminal, launch the tunnel
slink 5173 --subdomain react-preview

// Output:
// ➔ Tunnel established: https://react-preview.synapselink.cloud
// ➔ Routing traffic directly to http://localhost:5173`
    },
    express: {
        name: "Express / Node",
        port: "8080",
        cmd: "synapselink 8080 --subdomain local-api",
        code: `// Start your node express app
node server.js

// In a new terminal, launch the tunnel
slink 8080 --subdomain local-api

// Output:
// ➔ Tunnel established: https://local-api.synapselink.cloud
// ➔ Routing traffic directly to http://localhost:8080`
    },
    docker: {
        name: "Docker Containers",
        port: "8000",
        cmd: "synapselink 8000 --host dev-db-container",
        code: `// Launch local docker container exposing app port
docker run -p 8000:8000 my-web-app

// Route external traffic straight to docker instance host
slink 8000 --host dev-db-container

// Output:
// ➔ Tunnel established: https://random-id.synapselink.cloud
// ➔ Routing traffic directly to http://dev-db-container:8000`
    }
};

const FAQS = [
    {
        q: "What is a localhost tunnel and how does it work?",
        a: "A localhost tunnel acts as a secure intermediary bridge. It exposes a local application server (e.g. port 3000) to the public internet by forwarding requests down an established, persistent outbound WebSocket connection. This lets outside clients or APIs query your computer without your router needing port forwarding or public static IPs."
    },
    {
        q: "Is tunneling local development environments with SynapseLink secure?",
        a: "Yes, SynapseLink is designed with security in mind. Our tunnel utilizes outbound WebSocket streams, meaning your firewall does not need to accept incoming connection requests—negating malicious probe attacks. Traffic is encrypted in transit and terminated at our secure cloud ingress node, isolating your internal network."
    },
    {
        q: "How does SynapseLink handle webhook validation (like Stripe, Twilio or GitHub)?",
        a: "SynapseLink captures all incoming HTTP requests targeting your public subdomain. Our built-in request inspector records headers, path variables, query strings, and request bodies. The local CLI client proxies this data to your local API. You can read, debug, and replay the raw webhook trigger right from your dashboard."
    },
    {
        q: "Does SynapseLink support custom domains and SSL out of the box?",
        a: "Yes. In addition to our free random or persistent subdomains under synapselink.cloud, you can connect your own custom domain names. SynapseLink automatically handles Let's Encrypt SSL/TLS cert provisioning and renewals on our edge network at no additional cost."
    },
    {
        q: "What protocols are supported by SynapseLink?",
        a: "SynapseLink supports HTTP/1.1, HTTP/2, TCP stream relays, and UDP routing. This allows you to tunnel web apps, API microservices, database connections, and custom sockets or gaming servers depending on your developer requirements."
    }
];

const STEPS = [
    {
        num: "01",
        title: "CLI Tunnel Handshake",
        role: "Authentication & Setup",
        desc: "You run `synapselink 3000` or specify a subdomain. The CLI authenticates using your local configuration key and initiates a persistent, encrypted WebSocket connection to our global ingress relay.",
        command: "synapselink 3000 --subdomain dev-api",
        detail: "The relay validates the API key, checks that the subdomain is either free or reserved to your account, and holds the connection socket open."
    },
    {
        num: "02",
        title: "Public Ingress Hit",
        role: "Request Interception",
        desc: "An external service (like a Stripe webhook, mobile client, or browser) hits your public URL at `https://dev-api.synapselink.cloud/v1/users`.",
        command: "curl https://dev-api.synapselink.cloud/v1/users",
        detail: "Our edge nodes inspect rate limits, check password policies, and capture request metadata (method, headers, cookies, query parameters, body)."
    },
    {
        num: "03",
        title: "WebSocket Payload Push",
        role: "Forwarding Pipeline",
        desc: "The relay server serializes the incoming HTTP request payload and pushes it immediately down the open WebSocket connection to your active local CLI client.",
        command: "▸ Dispatching payload to WS client...",
        detail: "This secure channel acts as a bypass, allowing outside web traffic to interact with your local development environment without opening firewall ports."
    },
    {
        num: "04",
        title: "Local Proxying & Response",
        role: "Workstation Loopback",
        desc: "The CLI client receives the payload and issues a duplicate local HTTP request to your workstation's target server (e.g. `http://localhost:3000`). It receives the response and sends it back over the WebSocket to the relay, which forwards it to the client.",
        command: "HTTP/1.1 200 OK  [18ms]",
        detail: "The live request inspector updates instantly with full details, and the external client receives the response as if your server was deployed in the cloud."
    }
];

export default function HowItWorksPage() {
    const [activeStep, setActiveStep] = useState(0);
    const [openFaq, setOpenFaq] = useState(null);
    const [activeTab, setActiveTab] = useState("nextjs");

    const toggleFaq = (index) => {
        setOpenFaq(openFaq === index ? null : index);
    };

    useEffect(() => {
        document.title = "How SynapseLink Works - Secure Localhost Tunneling Architecture";
        
        const metaDesc = document.querySelector('meta[name="description"]');
        if (metaDesc) {
            metaDesc.setAttribute("content", "Discover how SynapseLink's secure reverse proxy, encrypted WebSockets, and edge ingress routing let you tunnel localhost ports and webhooks safely.");
        }

        const schemaId = "faq-schema-how-it-works";
        let script = document.getElementById(schemaId);
        if (!script) {
            script = document.createElement("script");
            script.id = schemaId;
            script.type = "application/ld+json";
            script.innerHTML = JSON.stringify({
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": FAQS.map(faq => ({
                    "@type": "Question",
                    "name": faq.q,
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": faq.a
                    }
                }))
            });
            document.head.appendChild(script);
        }

        return () => {
            const scriptToRemove = document.getElementById(schemaId);
            if (scriptToRemove) {
                scriptToRemove.remove();
            }
        };
    }, []);

    return (
        <PublicLayout>
            <div className="max-w-[1400px] mx-auto px-6 py-16">
                
                {/* Header */}
                <div className="text-center max-w-3xl mx-auto mb-16">
                    <div className="label-mono text-[#00FF41] mb-3">// system architecture</div>
                    <h1 className="text-4xl md:text-5xl font-display font-black tracking-tight mb-6 uppercase">
                        UNDER THE HOOD
                    </h1>
                    <p className="text-[#A1A1AA] text-sm md:text-base leading-relaxed">
                        SynapseLink acts as a zero-configuration proxy relay, bypassing firewalls and routers by establishing bidirectional secure multiplexed streams.
                    </p>
                </div>

                {/* Cyber Architecture Diagram Visualizer */}
                <div className="border border-[#27272A] bg-[#0A0A0A] p-8 mb-16">
                    <div className="label-mono text-[#00FF41] mb-6">// relay architecture flow</div>
                    
                    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-center">
                        
                        {/* 1. Client */}
                        <div className="border border-[#27272A] bg-[#050505] p-5 text-center flex flex-col items-center">
                            <Globe className="w-8 h-8 text-[#A1A1AA] mb-3" />
                            <div className="font-bold text-xs uppercase tracking-wider text-[#FAFAFA]">Public Client</div>
                            <div className="text-[10px] text-[#52525B] mt-1">Browser / Webhook API</div>
                            <div className="border border-[#27272A] bg-[#0A0A0A] p-2 mt-4 text-[9px] font-mono text-left w-full break-all text-[#00FF41]">
                                https://dev.synapselink.cloud
                            </div>
                        </div>

                        {/* Arrow */}
                        <div className="hidden lg:flex flex-col items-center justify-center text-[#52525B]">
                            <ArrowRight className="w-6 h-6 animate-pulse text-[#00FF41]" />
                            <span className="text-[9px] uppercase tracking-widest mt-1">HTTP Ingress</span>
                        </div>

                        {/* 2. SynapseLink Ingress Relay */}
                        <div className="border-2 border-[#00FF41] bg-[#0A0A0A] p-5 text-center flex flex-col items-center relative">
                            <div className="absolute top-0 right-0 bg-[#00FF41] text-black font-black text-[8px] px-1.5 py-0.5 uppercase tracking-widest">
                                relay edge
                            </div>
                            <Server className="w-8 h-8 text-[#00FF41] mb-3 crt-flicker" />
                            <div className="font-bold text-xs uppercase tracking-wider text-[#FAFAFA]">Ingress Relay</div>
                            <div className="text-[10px] text-[#52525B] mt-1">SSL & Route Gateway</div>
                            <div className="flex gap-1.5 mt-4">
                                <span className="border border-[#FF9900]/40 text-[#FF9900] text-[8px] uppercase px-1 py-0.5 font-mono">Basic Auth</span>
                                <span className="border border-[#00FF41]/40 text-[#00FF41] text-[8px] uppercase px-1 py-0.5 font-mono">Edge Shield</span>
                            </div>
                        </div>

                        {/* Arrow */}
                        <div className="hidden lg:flex flex-col items-center justify-center text-[#52525B]">
                            <ArrowRight className="w-6 h-6 animate-pulse text-[#00FF41] rotate-180 lg:rotate-0" />
                            <span className="text-[9px] uppercase tracking-widest mt-1">WS Control</span>
                        </div>

                        {/* 3. Dev Workstation */}
                        <div className="border border-[#27272A] bg-[#050505] p-5 text-center flex flex-col items-center">
                            <Laptop className="w-8 h-8 text-[#A1A1AA] mb-3" />
                            <div className="font-bold text-xs uppercase tracking-wider text-[#FAFAFA]">Workstation</div>
                            <div className="text-[10px] text-[#52525B] mt-1">slink CLI + Local Server</div>
                            <div className="border border-[#27272A] bg-[#0A0A0A] p-2 mt-4 text-[9px] font-mono text-left w-full break-all text-[#FF9900]">
                                http://localhost:3000
                            </div>
                        </div>

                    </div>

                    <div className="mt-8 pt-6 border-t border-[#27272A] text-center">
                        <pre className="inline-block text-[#52525B] text-[10px] font-mono text-left max-w-full overflow-x-auto leading-relaxed">
{`   +----------------+           HTTP Hit           +-------------------+          WS Push           +----------------+
   | External API/  | ==========================> | SynapseLink       | =========================> | slink CLI      |
   | Stripe Webhook |                             | Ingress Relay     |                            | Proxy Daemon   |
   +----------------+                             +-------------------+                            +----------------+
                                                            ||                                             ||
                                                            || Send Response                               || Local Request
                                                            \\/                                             \\/
   +----------------+       200 OK Response       +-------------------+      Send Response WS      +----------------+
   | Client Receives| <========================== | Public HTTP Gate  | <========================= | Localhost Server|
   | Payload Data   |                             | Router Engine     |                            | (e.g. Port 3000)|
   +----------------+                             +-------------------+                            +----------------+`}
                        </pre>
                    </div>
                </div>

                {/* Step-by-Step Request Lifecycle Simulator */}
                <div className="grid lg:grid-cols-12 gap-8 items-start mb-20">
                    
                    {/* Left Steps Menu */}
                    <div className="lg:col-span-4 space-y-3">
                        <div className="label-mono text-[#00FF41] mb-2">// request lifecycle</div>
                        {STEPS.map((step, idx) => (
                            <button
                                key={idx}
                                onClick={() => setActiveStep(idx)}
                                className={`w-full text-left p-4 border transition-all duration-150 flex items-center gap-4 ${activeStep === idx ? "bg-[#0F0F0F] border-[#00FF41]" : "bg-[#0A0A0A] border-[#27272A] hover:bg-[#050505]"}`}
                            >
                                <span className={`font-display text-2xl font-black ${activeStep === idx ? "text-[#00FF41]" : "text-[#52525B]"}`}>{step.num}</span>
                                <div>
                                    <div className="font-bold text-xs uppercase tracking-wider text-[#FAFAFA]">{step.title}</div>
                                    <div className="text-[9px] text-[#52525B] font-mono uppercase mt-0.5">{step.role}</div>
                                </div>
                            </button>
                        ))}
                    </div>

                    {/* Right Step Detail Display Panel */}
                    <div className="lg:col-span-8 border border-[#27272A] bg-[#0A0A0A] p-8 min-h-[310px] flex flex-col justify-between">
                        <div>
                            <div className="flex justify-between items-center border-b border-[#27272A] pb-3 mb-6">
                                <span className="label-mono text-[#00FF41]">STEP {STEPS[activeStep].num} · {STEPS[activeStep].role}</span>
                                <span className="text-[10px] text-[#52525B] font-bold">SYNAPSELINK PIPELINE</span>
                            </div>
                            <h3 className="heading-md text-[#FAFAFA] mb-4">{STEPS[activeStep].title}</h3>
                            <p className="text-sm text-[#A1A1AA] leading-relaxed mb-6">
                                {STEPS[activeStep].desc}
                            </p>
                            <p className="text-xs text-[#52525B] leading-relaxed italic">
                                {STEPS[activeStep].detail}
                            </p>
                        </div>

                        {/* Terminal Box */}
                        <div className="mt-8 border border-[#27272A] bg-[#050505] p-3 text-[10px] font-mono">
                            <div className="flex items-center gap-1.5 mb-2 border-b border-[#18181B] pb-1.5 text-[#52525B]">
                                <Terminal className="w-3.5 h-3.5" />
                                <span>terminal - log</span>
                            </div>
                            <div className="text-[#00FF41]">{STEPS[activeStep].command}</div>
                        </div>
                    </div>

                </div>

                {/* Cyber Security Details Section */}
                <div className="grid md:grid-cols-2 gap-8 mb-16">
                    <div className="border border-[#27272A] bg-[#0A0A0A] p-6">
                        <Shield className="w-8 h-8 text-[#00FF41] mb-4" />
                        <h4 className="font-display font-bold uppercase text-base text-[#FAFAFA] mb-2">Zero Ingress Ports</h4>
                        <p className="text-xs text-[#A1A1AA] leading-relaxed">
                            Bypass complex network configurations. The slink CLI initiates the outgoing TCP connection. That means you never have to configure NAT port-forwarding, write router policies, or modify local firewalls to allow inbound internet requests.
                        </p>
                    </div>
                    <div className="border border-[#27272A] bg-[#0A0A0A] p-6">
                        <Zap className="w-8 h-8 text-[#FF9900] mb-4" />
                        <h4 className="font-display font-bold uppercase text-base text-[#FAFAFA] mb-2">Edge TLS termination</h4>
                        <p className="text-xs text-[#A1A1AA] leading-relaxed">
                            SynapseLink terminates HTTPS traffic securely on our edge gateway servers. Requests are immediately proxied down to your local machine via our encrypted WebSocket channel. Binds support local HTTP, HTTPS, and API payloads safely.
                        </p>
                    </div>
                </div>

                {/* Section 1: Protocol Stack */}
                <div className="border border-[#27272A] bg-[#0A0A0A] p-8 mb-16">
                    <div className="label-mono text-[#00FF41] mb-6">// zero-trust ingress protocol stack</div>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
                        <div className="lg:col-span-1">
                            <h2 className="text-2xl font-display font-bold uppercase tracking-tight text-[#FAFAFA] mb-4">
                                Technical Architecture
                            </h2>
                            <p className="text-xs text-[#A1A1AA] leading-relaxed mb-4">
                                SynapseLink builds a high-performance reverse proxy bridge utilizing state-of-the-art tunneling protocols. By mapping incoming network events directly to multiplexed streams, we achieve single-digit millisecond latency overhead.
                            </p>
                            <p className="text-xs text-[#A1A1AA] leading-relaxed">
                                Rather than forwarding raw TCP traffic blindly, our edge proxy performs application-layer HTTP protocol analysis, providing built-in rate-limiting, SSL termination, and host verification before traffic touches your workstation.
                            </p>
                        </div>
                        <div className="lg:col-span-2 overflow-x-auto">
                            <table className="w-full text-left border border-[#27272A] text-xs">
                                <thead>
                                    <tr className="bg-[#121212] border-b border-[#27272A]">
                                        <th className="p-3 font-mono text-[#00FF41] uppercase tracking-wider">Network Tier</th>
                                        <th className="p-3 font-mono text-[#FAFAFA] uppercase tracking-wider">Protocol</th>
                                        <th className="p-3 font-mono text-[#A1A1AA] uppercase tracking-wider">Function</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-[#27272A] bg-[#050505] font-mono">
                                    <tr>
                                        <td className="p-3 font-bold text-[#FAFAFA]">Public Client -&gt; Edge Ingress</td>
                                        <td className="p-3 text-[#FF9900]">HTTPS / HTTP/2 / TLS 1.3</td>
                                        <td className="p-3 text-[#A1A1AA]">Public web clients or third-party webhooks hit the routing nodes.</td>
                                    </tr>
                                    <tr>
                                        <td className="p-3 font-bold text-[#FAFAFA]">Edge Gateway -&gt; CLI Client</td>
                                        <td className="p-3 text-[#00FF41]">Multiplexed WSS (Secure WebSocket)</td>
                                        <td className="p-3 text-[#A1A1AA]">Packets are marshaled and streamed bidirectionally over a single persistent TCP port.</td>
                                    </tr>
                                    <tr>
                                        <td className="p-3 font-bold text-[#FAFAFA]">CLI Client -&gt; Localhost Server</td>
                                        <td className="p-3 text-[#FF9900]">Local HTTP/1.1 / TCP Loopback</td>
                                        <td className="p-3 text-[#A1A1AA]">The local proxy agent replicates the call to localhost ports (e.g., 3000, 8080).</td>
                                    </tr>
                                    <tr>
                                        <td className="p-3 font-bold text-[#FAFAFA]">Control Plane Channel</td>
                                        <td className="p-3 text-[#00FF41]">gRPC over TLS</td>
                                        <td className="p-3 text-[#A1A1AA]">Handles API key validation, active connection heartbeat, and dynamic routing updates.</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* Section 2: Comparison Matrix */}
                <div className="border border-[#27272A] bg-[#0A0A0A] p-8 mb-16">
                    <div className="label-mono text-[#00FF41] mb-6">// tunnel vs port-forward vs vpn</div>
                    <div className="text-center max-w-2xl mx-auto mb-10">
                        <h2 className="text-2xl font-display font-bold uppercase tracking-tight text-[#FAFAFA] mb-3">
                            Why Modern Teams Choose Tunnels
                        </h2>
                        <p className="text-xs text-[#A1A1AA] leading-relaxed">
                            Exposing local services can introduce high security risks. Here is how SynapseLink's secure tunneling stacks up against traditional network methods.
                        </p>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {/* SynapseLink */}
                        <div className="border border-[#00FF41] bg-[#0A0A0A] p-6 relative">
                            <div className="absolute top-0 right-0 bg-[#00FF41] text-black font-mono text-[9px] font-bold px-2 py-0.5 uppercase">
                                Recommended
                            </div>
                            <div className="flex items-center gap-2 mb-4">
                                <Network className="w-5 h-5 text-[#00FF41]" />
                                <h3 className="font-display font-bold uppercase text-[#FAFAFA]">SynapseLink Tunnel</h3>
                            </div>
                            <ul className="space-y-3.5 text-xs text-[#A1A1AA] font-mono">
                                <li className="flex items-start gap-2">
                                    <span className="text-[#00FF41]">✔</span>
                                    <span><strong>Security:</strong> Outbound-only connection. Ports remain 100% closed to the internet.</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-[#00FF41]">✔</span>
                                    <span><strong>Configuration:</strong> Zero router or firewall changes. Single CLI command.</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-[#00FF41]">✔</span>
                                    <span><strong>SSL &amp; Subdomains:</strong> Automatic HTTPS certs with persistent reserved URLs.</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-[#00FF41]">✔</span>
                                    <span><strong>Collaboration:</strong> Easy public sharing link for external clients or APIs.</span>
                                </li>
                            </ul>
                        </div>

                        {/* Port Forwarding */}
                        <div className="border border-[#27272A] bg-[#050505] p-6">
                            <div className="flex items-center gap-2 mb-4">
                                <Settings className="w-5 h-5 text-[#FF9900]" />
                                <h3 className="font-display font-bold uppercase text-[#FAFAFA]">Port Forwarding</h3>
                            </div>
                            <ul className="space-y-3.5 text-xs text-[#A1A1AA] font-mono">
                                <li className="flex items-start gap-2">
                                    <span className="text-[#FF9900]">⚠</span>
                                    <span><strong>Security:</strong> Dangerous. Exposes local network directly to public IP scans.</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-[#FF9900]">⚠</span>
                                    <span><strong>Configuration:</strong> Requires router access, static local IP, and NAT rules.</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-[#FF9900]">⚠</span>
                                    <span><strong>SSL &amp; Subdomains:</strong> Manual cert generation (Let's Encrypt) and Dynamic DNS setup.</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-[#FF9900]">⚠</span>
                                    <span><strong>Collaboration:</strong> Hard. Exposes raw IP to clients; ISP might block port 80/443.</span>
                                </li>
                            </ul>
                        </div>

                        {/* Corporate VPN */}
                        <div className="border border-[#27272A] bg-[#050505] p-6">
                            <div className="flex items-center gap-2 mb-4">
                                <Shield className="w-5 h-5 text-[#52525B]" />
                                <h3 className="font-display font-bold uppercase text-[#FAFAFA]">Virtual Private Network</h3>
                            </div>
                            <ul className="space-y-3.5 text-xs text-[#A1A1AA] font-mono">
                                <li className="flex items-start gap-2">
                                    <span className="text-[#52525B]">ℹ</span>
                                    <span><strong>Security:</strong> Safe, but requires adding users directly into your private network.</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-[#52525B]">ℹ</span>
                                    <span><strong>Configuration:</strong> Heavy IT footprint. VPN profiles, keys, and client app installations.</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-[#52525B]">ℹ</span>
                                    <span><strong>SSL &amp; Subdomains:</strong> Uses internal domain names or raw private IPs. No public HTTPS.</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-[#52525B]">ℹ</span>
                                    <span><strong>Collaboration:</strong> Impossible for third-party webhooks (Stripe cannot join your private VPN).</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>

                {/* Section 3: Interactive FAQ Accordion */}
                <div className="border border-[#27272A] bg-[#0A0A0A] p-8 mb-16">
                    <div className="label-mono text-[#00FF41] mb-6">// faq - answer engine database</div>
                    <div className="max-w-3xl mx-auto">
                        <h2 className="text-2xl font-display font-bold uppercase tracking-tight text-[#FAFAFA] text-center mb-8">
                            Frequently Answered Queries
                        </h2>
                        <div className="space-y-4">
                            {FAQS.map((faq, idx) => {
                                const isOpen = openFaq === idx;
                                return (
                                    <div 
                                        key={idx} 
                                        className={`border transition-colors duration-200 ${isOpen ? "border-[#00FF41] bg-[#0F0F0F]" : "border-[#27272A] bg-[#050505] hover:border-[#52525B]"}`}
                                    >
                                        <button
                                            onClick={() => toggleFaq(idx)}
                                            className="w-full flex items-center justify-between p-5 text-left font-bold text-xs sm:text-sm uppercase tracking-wider text-[#FAFAFA]"
                                        >
                                            <span className="flex items-center gap-3">
                                                <HelpCircle className={`w-4 h-4 transition-colors ${isOpen ? "text-[#00FF41]" : "text-[#52525B]"}`} />
                                                {faq.q}
                                            </span>
                                            {isOpen ? (
                                                <ChevronUp className="w-4 h-4 text-[#00FF41]" />
                                            ) : (
                                                <ChevronDown className="w-4 h-4 text-[#52525B]" />
                                            )}
                                        </button>
                                        {isOpen && (
                                            <div className="px-5 pb-5 pt-0 text-xs sm:text-sm text-[#A1A1AA] leading-relaxed font-mono border-t border-[#27272A]/50 mt-2">
                                                <div className="pt-3">{faq.a}</div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Section 4: Framework-specific CLI Quickstarts */}
                <div className="border border-[#27272A] bg-[#0A0A0A] p-8 mb-8">
                    <div className="label-mono text-[#00FF41] mb-6">// dynamic framework setup</div>
                    
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                        <div className="lg:col-span-5">
                            <h2 className="text-2xl font-display font-bold uppercase tracking-tight text-[#FAFAFA] mb-4">
                                Expose Any Local Environment
                            </h2>
                            <p className="text-xs text-[#A1A1AA] leading-relaxed mb-6">
                                SynapseLink is framework-agnostic. Whether you're running a modern frontend application, a containerized database, or an API backend in Python, Go, or Node.js, we construct instant connections in seconds.
                            </p>
                            <div className="flex flex-wrap gap-2">
                                {Object.keys(FRAMEWORKS).map((key) => (
                                    <button
                                        key={key}
                                        onClick={() => setActiveTab(key)}
                                        className={`text-[10px] uppercase font-bold tracking-widest px-3.5 py-2 border transition-all duration-150 ${activeTab === key ? "bg-[#00FF41] text-black border-[#00FF41]" : "bg-[#050505] text-[#A1A1AA] border-[#27272A] hover:bg-[#0F0F0F] hover:text-[#FAFAFA]"}`}
                                    >
                                        {FRAMEWORKS[key].name}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="lg:col-span-7 border border-[#27272A] bg-[#050505] p-5 font-mono text-[10px] leading-relaxed">
                            <div className="flex items-center justify-between border-b border-[#27272A] pb-3 mb-4 text-[#52525B]">
                                <div className="flex items-center gap-1.5">
                                    <Code className="w-3.5 h-3.5" />
                                    <span>how-to-{activeTab}.sh</span>
                                </div>
                                <span className="uppercase text-[9px] tracking-widest text-[#00FF41]">Active Config</span>
                            </div>
                            <pre className="text-[#A1A1AA] overflow-x-auto whitespace-pre-wrap">
                                {FRAMEWORKS[activeTab].code}
                            </pre>
                        </div>
                    </div>
                </div>

            </div>
        </PublicLayout>
    );
}
